import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';
import TideForms from '../marinetools/components/tides/TideForms.jsx';
import tidesSecondaryPorts from '../data/tidesSecondaryPorts.js';

export default function TidesSecondaryPorts() {
  return (
    <section className="as-section">
      <div className="container">
        <h1 className="as-page-title">Tides &ndash; Secondary Ports</h1>
        <p className="as-lead">
          For the purposes of this module and its exercises, we will be using the 2025
          Queensland Tide Tables published by the Queensland Government. You should
          download this pdf and use it as the tide reference with this module.
        </p>
        <p>This module assumes that you have completed the previous modules Tides and Tides &ndash; Standard Port.</p>
        <p>
          Secondary ports are those for which daily predictions are not provided in the
          Queensland Tide Tables. These locations are grouped and associated to the
          adjacent standard port with a similar tidal pattern. Data sufficient for
          calculating their times and heights is supplied following the standard port
          prediction tables.
        </p>
        <p>
          Tidal Levels &ndash; A list of tidal levels referred to Queensland Port Datum
          for standard ports and selected secondary places is given in the following
          tables:
        </p>
        <ul>
          <li>
            <strong>Semidiurnal Tide Planes</strong> &ndash; refers to a tide which has a
            period or cycle of approximately half of one tidal day (about 12.5 hours).
            Semidiurnal tides usually have two high and two low tides each day. The tides
            at Brisbane Bar are a typical example of semidiurnal tides.
          </li>
          <li>
            <strong>Diurnal Tide Planes</strong> &ndash; refers to a tide which has a
            period or cycle of approximately one tidal day (about 25 hours). Diurnal
            tides usually have one high and one low tide each day. The tides at Karumba
            are a typical example of diurnal tides.
          </li>
        </ul>
        <p>
          In addition, the tables for semidiurnal and diurnal tidal planes provide the
          factors necessary to calculate tidal predictions at the selected secondary
          places (referred to Queensland Port Datum at each secondary place) from the
          tidal predictions of the standard ports.
        </p>
        <p>
          Find the required locality in the Semidiurnal Tidal Planes table or the Diurnal
          Tidal Planes table using the 2025 Queensland Tide Tables and note its standard
          port:
        </p>

        <h3 className="as-subheading-sm">Time of High Water</h3>
        <ul>
          <li>Note the time difference in column 1;</li>
          <li>Add or subtract (as indicated by + or &minus;) this time difference to the predicted time of high water at the standard port.</li>
        </ul>

        <h3 className="as-subheading-sm">Time of Low Water</h3>
        <ul>
          <li>Note the time difference in column 2;</li>
          <li>Add or subtract (as indicated by + or &minus;) this time difference to the predicted time of low water at the standard port. The result is the approximate time of the tide at the required locality.</li>
        </ul>

        <h3 className="as-subheading-sm">Height of High Water</h3>
        <ul>
          <li>Find the height of the predicted high water at the standard port;</li>
          <li>Multiply the height by the ratio in column 8. If Ratio is blank, use value for Standard Port which in this case is Brisbane Bar with Ratio of 1.00.</li>
          <li>Add or subtract (as indicated by the + or &minus;) the constant in column 9. If Constant is blank, use value for Standard Port which in this case is Brisbane Bar with Constant of 0.</li>
        </ul>

        <h3 className="as-subheading-sm">Height of Low Water</h3>
        <ul>
          <li>Find the height of the predicted low water at the standard port;</li>
          <li>Multiply the height by the ratio in column 8. If Ratio is blank, use value for Standard Port which in this case is Brisbane Bar with Ratio of 1.00.</li>
          <li>Add or subtract (as indicated by the + or &minus;) the constant in column 9. If Constant is blank, use value for Standard Port which in this case is Brisbane Bar with Constant of 0.</li>
        </ul>
        <p>
          The result is the approximate height of tide at the required locality. Once you
          have calculated the HW and LW times and heights for your secondary port, you can
          use the Standard Tidal Curve exactly as described in the previous module to find
          the tide height at any intermediate time.
        </p>

        <h2 className="as-subheading">Example Tide Calculation for Secondary Ports</h2>

        <h3 className="as-subheading-sm">Standard Port Tides</h3>
        <p>Following is an example extract of Tide times and levels on 16th of April 2025 for Brisbane Bar which is our Standard Port in this example:</p>
        <Table bordered responsive className="as-data-table">
          <thead><tr><th>Description</th><th>Time</th><th>Metres from (LAT)</th></tr></thead>
          <tbody>
            <tr><td>Low Water (LW)</td><td>05:32</td><td>0.73m</td></tr>
            <tr><td>High Water (HW)</td><td>11:04</td><td>1.88m</td></tr>
            <tr><td>Low Water (LW)</td><td>17:12</td><td>0.56m</td></tr>
            <tr><td>High Water (HW)</td><td>23:41</td><td>2.33m</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Secondary Port Differences</h3>
        <p>
          We are going to find the tide levels for Rocky Point which is our Secondary
          Port. Time and height differences derived from the Semidiurnal Tidal Planes
          table for Brisbane Bar:
        </p>
        <Table bordered responsive className="as-data-table">
          <tbody>
            <tr><td>Time difference High Water (Column 1)</td><td>+0:40</td></tr>
            <tr><td>Time difference Low Water (Column 2)</td><td>+0:55</td></tr>
            <tr><td>Ratio (Column 8)</td><td>0.96</td></tr>
            <tr><td>Constant (Column 9)</td><td>+0.01</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Predicted HIGH water at Secondary Port (Rocky Point)</h3>
        <Table bordered responsive className="as-data-table">
          <tbody>
            <tr><td>Predicted High Water at Brisbane Bar (Standard Port)</td><td>1.88m at 11:04</td></tr>
            <tr><td>Time of High Water at Rocky Point (Secondary Port)</td><td>11:04 + 0:40 = 11:44</td></tr>
            <tr><td>Height of High Water at Rocky Point ((HW &times; Ratio) + Constant)</td><td>(1.88m &times; 0.96) + 0.01 = 1.81m + 0.01 = 1.82m</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Predicted LOW water at Secondary Port (Rocky Point)</h3>
        <Table bordered responsive className="as-data-table">
          <tbody>
            <tr><td>Predicted Low Water at Brisbane Bar (Standard Port)</td><td>0.73m at 05:32</td></tr>
            <tr><td>Time of Low Water at Rocky Point (Secondary Port)</td><td>05:32 + 0:55 = 06:27</td></tr>
            <tr><td>Height of Low Water at Rocky Point ((LW &times; Ratio) + Constant)</td><td>(0.73m &times; 0.96) + 0.01 = 0.71m + 0.01 = 0.72m</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Calculating Tide Range for Secondary Port</h3>
        <p>
          The range of the tide is the difference between the height of high water and
          the next succeeding or last preceding low water for Rocky Point (Secondary
          Port). Using above example to find the tide height difference (Tide Range) for
          16th of April 2025 for 09:30, subtract the low water from high water to get the
          range:
        </p>
        <Table bordered responsive className="as-data-table">
          <tbody>
            <tr><td>High Water</td><td>1.82m</td></tr>
            <tr><td>Low Water</td><td>0.72m</td></tr>
            <tr><td>Range (Height Difference)</td><td>1.10m</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Calculating intermediate height</h3>
        <p>Continuing with above example, we need to find tidal height at 09:30 taking following steps:</p>
        <ul>
          <li>Find time difference from High Water: 11:44 (HW) &ndash; 09:30 (Desired Time) = 2 hours and 14 minutes before high water.</li>
          <li>Tidal Range: 1.82m (HW) &ndash; 0.72m (LW) = 1.10m</li>
          <li>Using the 3m Standard Tidal Curve graph, find the 2 hours 14 minutes mark before high water on the horizontal axis (red line on the graph).</li>
          <li>Follow the line up to the projected 1.10m tidal curve and read the corresponding height on the vertical axis (blue line on the graph).</li>
        </ul>
        <p>In this scenario, the curve shows the height to be added to the low water is approximately 0.80m</p>

        <h3 className="as-subheading-sm">Calculate Actual Height</h3>
        <ul>
          <li>Height at 09:30 am = Low Water Height + Curve Height</li>
          <li>Result: 0.72m + 0.80m = 1.52m</li>
        </ul>
        <p>At 09:30, the tide height is approximately 1.52 meters above the Lowest Astronomical Tide (LAT)</p>

        <h2 className="as-subheading">Progress Check</h2>
        <p>
          Complete the exercises in each tab to check your progress for Secondary Ports.
          Please use the Queensland MSQ 2025 Tide Tables as the exclusive reference in all
          the exercises. Any tide times should be formatted as HH:mm (Example: 09:35).
          This would typically show as 0935 in the Queensland Tide tables.
        </p>

        <TideForms data={tidesSecondaryPorts} />

        <div className="d-flex justify-content-between mt-4">
          <Link to="/tides-standard-ports" className="btn-as-outline">&larr; Previous &ndash; Standard Ports</Link>
          <Link to="/tides-under-keel-clearance" className="btn-as-primary">Next &ndash; Under-Keel Clearance (UKC)</Link>
        </div>
      </div>
    </section>
  );
}
